import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Checkbox, Collapse, Drawer, Grid, Input, Skeleton, Spin, Typography, message } from 'antd';
import { BulbOutlined, CheckCircleFilled, CloseCircleFilled, MinusCircleOutlined } from '@ant-design/icons';
import {
  EvaluationModelRole,
  IMPROVEMENT_ACTIONS,
  assessImprovement,
  describeCost,
  getModelPlan,
  parseProposal,
  proposeImprovement,
  type ControlOutcome,
  type EvaluationModelOption,
  type ImprovementAction,
  type ImprovementAssessment,
  type ImprovementLine,
  type ScenarioImprovementView,
} from '@/api/scenarioIntelligence';
import {
  checkScenarioVersion,
  getScenarioReview,
  getScenarioVersion,
  parseVersion,
  saveScenarioVersion,
  type EvaluationScenario,
  type ScenarioVersionForEdit,
} from '@/api/scenarioVersions';
import { ModelPickerDrawer, modelLine } from '@/components/ModelPickerDrawer';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { ScenarioMergeDrawer } from '../editor/editorDrawers';
import { cardStyle, surface, when } from '../review/reviewParts';
import { checkSentence, shortHash } from '../scenarioWords';
import { clearDraft, fieldOutcome, fieldsOf, loadDraft, saveDraft, type ImprovementField } from './improvement';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// Screen 04: improve one scenario with an AI action (accepted mockup 04-improvement-diff, revision 2).
// An action never edits a scenario: the Scenario model proposes lines, each with its reason; the person keeps or drops
// each one; the server assesses the version the kept lines make (its two controls replayed on stored text, its compile
// check, the approval a save loses) and the drawer shows that, field by field, as Original / Yours / Current. Saving is
// the editor's own save: a newer version saved meanwhile is a 409 that opens the three-way merge, and the merged result is
// checked before it can be saved. An unproven change is never saved in one click. The draft stays in this browser until
// it is saved or discarded.
// ─────────────────────────────────────────────────────────────────────────────

type Phase = 'choose' | 'proposing' | 'failed' | 'proposal';
interface Conflict { current: EvaluationScenario; hash: string; savedBy: number; savedAtUtc?: string | null }

const mono = { fontFamily: "'JetBrains Mono', monospace" } as const;

export const ImproveScenarioDrawer = observer(function ImproveScenarioDrawer({
  open,
  scenarioId,
  onClose,
  onSaved,
}: {
  open: boolean;
  scenarioId: string | null;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { themeStore, merchantContextStore: ctx } = useStore();
  const isDark = themeStore.mode === 'dark';
  const s = surface(isDark);
  const narrow = !Grid.useBreakpoint().md;
  const merchantId = ctx.selectedMerchantId;
  const practice = merchantId != null ? ctx.merchants.find((m) => m.id === merchantId) : undefined;

  const [opened, setOpened] = useState<ScenarioVersionForEdit | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [action, setAction] = useState<ImprovementAction>('StrengthenChecks');
  const [request, setRequest] = useState('');
  const [planModel, setPlanModel] = useState<EvaluationModelOption | null>(null);
  const [planProblem, setPlanProblem] = useState<string | null>(null);
  const [chosenModel, setChosenModel] = useState<EvaluationModelOption | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [phase, setPhase] = useState<Phase>('choose');
  const [failure, setFailure] = useState<string | null>(null);
  const [view, setView] = useState<ScenarioImprovementView | null>(null);
  const [kept, setKept] = useState<Set<string>>(new Set());
  const [assessment, setAssessment] = useState<ImprovementAssessment | null>(null);
  const [assessing, setAssessing] = useState(false);
  const [assessError, setAssessError] = useState<string | null>(null);
  const [restoredAt, setRestoredAt] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [refused, setRefused] = useState<string[] | null>(null);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [savedByName, setSavedByName] = useState<string | null>(null);
  const [merged, setMerged] = useState<{ version: EvaluationScenario; problems: string[] } | null>(null);
  const [unprovenOk, setUnprovenOk] = useState(false);
  const proposing = useRef<AbortController | null>(null);
  const assessSeq = useRef(0);

  // Open: read the version as it is now, the Scenario model the plan names, and any draft this browser kept.
  useEffect(() => {
    if (!open || !scenarioId) return;
    let cancelled = false;
    setOpened(null);
    setLoadError(null);
    setPhase('choose');
    setFailure(null);
    setView(null);
    setAssessment(null);
    setAssessError(null);
    setRefused(null);
    setConflict(null);
    setMerged(null);
    setUnprovenOk(false);
    setChosenModel(null);
    setRequest('');
    setRestoredAt(null);
    getScenarioVersion(scenarioId)
      .then((v) => {
        if (cancelled) return;
        setOpened(v);
        const draft = loadDraft(scenarioId);
        if (draft) {
          setView(draft.view);
          setKept(new Set(draft.kept));
          setAssessment(draft.view.assessment);
          setPhase('proposal');
          setRestoredAt(draft.keptAtUtc);
        }
      })
      .catch((e: Error) => !cancelled && setLoadError(e.message));
    if (merchantId != null) {
      getModelPlan(merchantId)
        .then((p) => {
          if (cancelled) return;
          const role = p.roles.find((r) => r.role === EvaluationModelRole.Scenario);
          setPlanModel(role?.row ?? null);
          setPlanProblem(role?.problem ?? null);
        })
        .catch((e: Error) => !cancelled && setPlanProblem(e.message));
    } else {
      setPlanProblem('Choose a practice first: the Scenario model runs on the practice’s own registered deployments.');
    }
    return () => {
      cancelled = true;
      proposing.current?.abort();
    };
  }, [open, scenarioId, merchantId]);

  const original = useMemo(() => (view ? parseVersion(view.originalJson) : opened?.version ?? null), [view, opened]);
  const proposal = useMemo(() => parseProposal(view?.proposalJson), [view]);
  const yours = useMemo(() => parseVersion(assessment?.versionJson), [assessment]);
  const fields = useMemo(() => (original && proposal ? fieldsOf(original, proposal.lines) : []), [original, proposal]);
  const stale = !!view && !!opened && opened.baseHash !== view.baseHash;

  // Every change to the kept lines is assessed by the server: the version they make, its controls, the approval effect.
  useEffect(() => {
    if (!view || !proposal || phase !== 'proposal') return;
    const mine = ++assessSeq.current;
    const keptIds = proposal.lines.filter((l) => kept.has(l.id)).map((l) => l.id);
    saveDraft(view.scenarioId, { view: { ...view, assessment: assessment ?? view.assessment }, kept: keptIds, keptAtUtc: new Date().toISOString() });
    if (keptIds.length === proposal.lines.length && assessment === view.assessment) return;
    setAssessing(true);
    const t = window.setTimeout(() => {
      assessImprovement({ agentId: view.agentId, scenarioId: view.scenarioId, baseHash: view.baseHash, proposalJson: view.proposalJson, keptLineIds: keptIds, merchantId })
        .then((a) => {
          if (mine !== assessSeq.current) return;
          setAssessment(a);
          setAssessError(null);
          setUnprovenOk(false);
        })
        .catch((e: Error) => mine === assessSeq.current && setAssessError(e.message))
        .finally(() => mine === assessSeq.current && setAssessing(false));
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- assessed when the kept lines change, not when the answer lands
  }, [kept, view, phase]);

  const propose = async () => {
    if (!opened || !scenarioId) return;
    proposing.current?.abort();
    const ctrl = new AbortController();
    proposing.current = ctrl;
    setPhase('proposing');
    setFailure(null);
    try {
      const v = await proposeImprovement({
        agentId: opened.agentId, scenarioId, baseHash: opened.baseHash, action, request: request.trim() || undefined,
        scenarioModelId: chosenModel?.aIModelId ?? null, merchantId,
      });
      if (ctrl.signal.aborted) return;
      setView(v);
      setAssessment(v.assessment);
      setKept(new Set(parseProposal(v.proposalJson)?.lines.map((l) => l.id) ?? []));
      setPhase('proposal');
      setRestoredAt(null);
    } catch (e) {
      if (ctrl.signal.aborted) return;
      setFailure((e as Error).message);
      setPhase('failed');
    }
  };

  const cancelProposing = () => {
    proposing.current?.abort();
    setPhase(view ? 'proposal' : 'choose');
  };

  const discard = () => {
    if (scenarioId) clearDraft(scenarioId);
    setView(null);
    setAssessment(null);
    setKept(new Set());
    setConflict(null);
    setMerged(null);
    setRefused(null);
    setRestoredAt(null);
    setPhase('choose');
  };

  const finishSaved = (outcome: 'Saved' | 'Unchanged') => {
    if (scenarioId) clearDraft(scenarioId);
    void message.success(outcome === 'Saved' ? 'Saved as a new version. It needs its own approval.' : 'Nothing changed, so no new version was made.');
    onSaved?.();
    onClose();
  };

  const save = async () => {
    const version = merged?.version ?? yours;
    const base = merged && conflict ? conflict.hash : view?.baseHash;
    if (!version || !base) return;
    setSaving(true);
    setRefused(null);
    try {
      const r = await saveScenarioVersion(version, base);
      if (r.outcome === 'Saved' || r.outcome === 'Unchanged') finishSaved(r.outcome);
      else if (r.outcome === 'Conflict' && r.current && r.hash) {
        setMerged(null);
        setConflict({ current: r.current, hash: r.hash, savedBy: r.currentSavedBy ?? 0, savedAtUtc: r.currentSavedAtUtc });
        setSavedByName(null);
        if (view) {
          getScenarioReview(view.agentId, view.scenarioId)
            .then((c) => setSavedByName(c.versions.find((v) => v.authorUserId === r.currentSavedBy)?.authorName ?? null))
            .catch(() => { /* the merge names them by id */ });
        }
      } else setRefused(r.problems.length ? r.problems : ['The version was not saved.']);
    } catch (e) {
      setRefused([(e as Error).message]);
    } finally {
      setSaving(false);
    }
  };

  const applyMerged = async (version: EvaluationScenario) => {
    setMergeOpen(false);
    try {
      const c = await checkScenarioVersion(version);
      setMerged({ version, problems: c.problems ?? [] });
    } catch (e) {
      setMerged({ version, problems: [(e as Error).message] });
    }
  };

  const toggle = (id: string, on: boolean) =>
    setKept((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });

  const editable = opened?.editable ?? false;
  const problems = merged ? merged.problems : assessment?.compileProblems ?? [];
  const keptCount = proposal?.lines.filter((l) => kept.has(l.id)).length ?? 0;
  const proven = assessment?.proven ?? false;
  const nextNumber = assessment?.nextVersionNumber;
  const saveLabel = nextNumber ? `Save version ${nextNumber}` : 'Save as a new version';
  const saveBlocked =
    !editable ? opened?.notEditableReason ?? 'This scenario cannot be saved here.'
      : keptCount === 0 ? 'Keep at least one line, or discard the draft.'
        : assessing ? 'Checking the kept lines…'
          : conflict && !merged ? 'Someone saved a newer version: merge first.'
            : problems.length > 0 ? 'The version does not validate yet.'
              : !proven && !unprovenOk ? 'Unproven: tick the confirmation above to save it anyway.'
                : null;

  const statusPill =
    phase === 'proposing' ? <StatusPill tone="amber">Drafting</StatusPill>
      : phase === 'failed' ? <StatusPill tone="red">No proposal</StatusPill>
        : phase === 'proposal' && merged ? <StatusPill tone={merged.problems.length ? 'red' : 'green'}>{merged.problems.length ? 'Merge does not validate' : 'Merged · not saved'}</StatusPill>
          : phase === 'proposal' && (conflict || stale) ? <StatusPill tone="amber">Draft is stale</StatusPill>
            : phase === 'proposal' ? <StatusPill tone="violet">AI proposal · not saved</StatusPill>
              : null;

  const baseNumber = assessment?.baseVersionNumber;
  const title = (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <BulbOutlined style={{ color: colors.violet[600] }} />
        <span>Improve scenario</span>
        {statusPill}
      </div>
      <Text type="secondary" style={{ fontSize: 12, fontWeight: 400, ...mono, wordBreak: 'break-all' }}>
        {scenarioId}
        {phase === 'proposal' && baseNumber ? ` · ${conflict || stale ? 'your draft is based on' : 'you opened'} version ${baseNumber}` : ''}
      </Text>
    </div>
  );

  const footer = phase === 'proposal' && view ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {!proven && assessment && keptCount > 0 && editable && (
        <Checkbox checked={unprovenOk} onChange={(e) => setUnprovenOk(e.target.checked)}>
          I have read why this change is unproven, and save it anyway as a new, unapproved version.
        </Checkbox>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <Text type="secondary" style={{ fontSize: 12, flex: '1 1 320px' }}>
          {saveBlocked && saveBlocked !== 'Checking the kept lines…'
            ? saveBlocked
            : `${nextNumber ? `Saves version ${nextNumber}` : 'Saves a new version'}, unapproved${assessment?.material ? ' because what is tested changed' : ''}. Results already recorded keep pointing at version ${baseNumber ?? 'the one you opened'}; nothing reruns by itself.`}
        </Text>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button onClick={discard} disabled={saving}>Discard draft</Button>
          <Button onClick={() => setKept(new Set())} disabled={saving || keptCount === 0}>Reject all</Button>
          <Button type="primary" loading={saving} disabled={!!saveBlocked} onClick={() => void save()}>{saveLabel}</Button>
        </div>
      </div>
    </div>
  ) : null;

  const modelName = modelLine(chosenModel ?? planModel);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="min(1120px, 100vw)"
      title={title}
      footer={footer}
      styles={{ body: { background: s.page, display: 'flex', flexDirection: 'column', gap: 12, padding: narrow ? 12 : 20 } }}
    >
      {loadError ? (
        <Alert type="error" showIcon title="Could not open this scenario" description={`The server said: “${loadError}”`} />
      ) : !opened ? (
        <div aria-busy="true">
          <Text strong style={{ display: 'block' }}>Opening {scenarioId}</Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12.5 }}>Reading the version you will improve and the Scenario model that drafts the change.</Text>
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ) : (
        <>
          {!editable && (
            <Alert type="info" showIcon title="Read only here"
              description={`${opened.notEditableReason ?? 'This scenario cannot be saved here.'} You can still ask for a proposal and read it; saving waits until it can be edited.`} />
          )}
          {restoredAt && phase === 'proposal' && (
            <Alert type="info" showIcon closable onClose={() => setRestoredAt(null)}
              title={`Your draft from ${when(restoredAt)} is back`}
              description="It was kept in this browser. Nothing of it was saved; discard it to start again." />
          )}

          {phase === 'choose' && (
            <ChooseAction action={action} onAction={setAction} request={request} onRequest={setRequest} isDark={isDark}
              modelName={modelName} chosen={!!chosenModel} planProblem={chosenModel ? null : planProblem}
              onChangeModel={() => setPickerOpen(true)} onPropose={() => void propose()} checks={original?.checks.length ?? 0} />
          )}

          {phase === 'proposing' && (
            <section style={cardStyle(isDark)} aria-busy="true" aria-label="Drafting">
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <Spin />
                <div style={{ flex: 1 }}>
                  <Text strong style={{ display: 'block' }}>
                    {`Drafting ${IMPROVEMENT_ACTIONS.find((a) => a.action === action)?.label.toLowerCase() ?? 'the change'} with ${modelName ?? 'the Scenario model'}`}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12.5 }}>
                    {`Reading ${original?.checks.length ?? 0} check${original?.checks.length === 1 ? '' : 's'} and what the user says; then replaying the controls on stored text. Nothing is saved.`}
                  </Text>
                </div>
                <Button onClick={cancelProposing}>Cancel</Button>
              </div>
            </section>
          )}

          {phase === 'failed' && (
            <Alert type="error" showIcon title="No proposal"
              description={
                <>
                  <div>{failure}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    <Button size="small" onClick={() => void propose()}>Try again</Button>
                    <Button size="small" onClick={() => setPickerOpen(true)}>Use another Scenario model…</Button>
                    <Button size="small" type="text" onClick={() => setPhase('choose')}>Back</Button>
                  </div>
                </>
              } />
          )}

          {phase === 'proposal' && view && proposal && original && (
            <>
              <WhyCard view={view} why={proposal.why} actionLabel={proposal.actionLabel} controls={assessment?.controls ?? []} proposal={proposal}
                assessing={assessing} isDark={isDark} />
              {assessError && <Alert type="error" showIcon title="The kept lines could not be checked" description={assessError} />}
              {assessment && keptCount > 0 && (
                <Alert
                  type={assessment.approval.wasApproved && assessment.approval.policy === 'Material' ? 'warning' : 'info'}
                  showIcon
                  title={
                    <>
                      <div style={{ fontSize: 13.5 }}>{assessment.approval.words}</div>
                      {assessment.approval.approvedHash && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {'The approval is bound to hash '}<span style={mono}>{shortHash(assessment.approval.approvedHash)}</span>
                          {'. A requirement approval is not touched by this save.'}
                        </Text>
                      )}
                    </>
                  }
                />
              )}
              {stale && !conflict && (
                <Alert type="warning" showIcon title={`Your draft is based on version ${baseNumber ?? '?'}; a newer version was saved since`}
                  description="Your draft is kept. Saving finds the newer version, writes nothing, and opens the three-way merge so nothing of either version is dropped silently." />
              )}
              {conflict && (
                <Alert type="warning" showIcon
                  title={`${savedByName ?? 'Someone'} saved a newer version while you worked on version ${baseNumber ?? '?'}`}
                  description={
                    <>
                      <div>Nothing was written. Your draft is kept. Merge it with theirs field by field: a field only one of you changed merges by itself; a field you both changed waits for your choice. The merged result is checked before it can be saved.</div>
                      <Button style={{ marginTop: 8 }} size="small" type="primary" onClick={() => setMergeOpen(true)}>Open the three-way merge</Button>
                    </>
                  } />
              )}
              {merged && (
                <Alert type={merged.problems.length ? 'error' : 'success'} showIcon
                  title={merged.problems.length ? 'The merged result does not validate' : 'Merged: the result validates'}
                  description={merged.problems.length
                    ? <><ul style={{ margin: 0, paddingLeft: 18 }}>{merged.problems.map((p) => <li key={p}>{p}</li>)}</ul><Text type="secondary" style={{ fontSize: 12 }}>Edit one side in the merge, or take one side for the field. Save stays off.</Text>
                      <div><Button size="small" style={{ marginTop: 6 }} onClick={() => setMergeOpen(true)}>Back to the merge</Button></div></>
                    : 'Review it, then save it as a new, unapproved version. Right before saving, Studio checks again that nobody saved a newer one.'} />
              )}
              {refused && (
                <Alert type="error" showIcon closable onClose={() => setRefused(null)} title="Not saved: your draft is kept"
                  description={<ul style={{ margin: 0, paddingLeft: 18 }}>{refused.map((p) => <li key={p}>{p}</li>)}</ul>} />
              )}
              {!merged && problems.length > 0 && keptCount > 0 && (
                <Alert type="error" showIcon title="The version your kept lines make does not validate"
                  description={<ul style={{ margin: 0, paddingLeft: 18 }}>{problems.map((p) => <li key={p}>{p}</li>)}</ul>} />
              )}
              {!narrow && <ColumnHeads isDark={isDark} baseNumber={baseNumber} conflict={conflict} />}
              {[...fields.filter((f) => f.lines.length > 0), ...fields.filter((f) => f.lines.length === 0)].map((f) => (
                <FieldRow key={f.key} field={f} original={original} current={conflict?.current ?? null} kept={kept} onToggle={toggle}
                  isDark={isDark} narrow={narrow} disabled={saving} />
              ))}
              {proposal.leftOut.length > 0 && (
                <Collapse size="small" items={[{
                  key: 'left',
                  label: `${proposal.leftOut.length} line${proposal.leftOut.length === 1 ? ' was' : 's were'} left out by the rules`,
                  children: <ul style={{ margin: 0, paddingLeft: 18 }}>{proposal.leftOut.map((l) => <li key={l}><Text style={{ fontSize: 12.5 }}>{l}</Text></li>)}</ul>,
                }]} />
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>More AI actions:</Text>
                {IMPROVEMENT_ACTIONS.filter((a) => a.action !== proposal.action).map((a) => (
                  <Button key={a.action} size="small" disabled={saving} onClick={() => { setAction(a.action); discard(); }}>{a.label}</Button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {pickerOpen && (
        <ModelPickerDrawer
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          role={EvaluationModelRole.Scenario}
          merchantId={merchantId}
          practiceLabel={practice ? `${practice.title} · #${practice.id}` : undefined}
          selectedId={(chosenModel ?? planModel)?.aIModelId ?? null}
          allowOff={false}
          onSelect={(o) => {
            setChosenModel(o && o.aIModelId !== planModel?.aIModelId ? o : null);
            setPickerOpen(false);
            if (phase === 'failed') setPhase('choose');
          }}
        />
      )}
      {conflict && original && yours && (
        <ScenarioMergeDrawer open={mergeOpen} onClose={() => setMergeOpen(false)} original={original} yours={yours} current={conflict.current}
          savedBy={savedByName ?? (conflict.savedBy ? `user #${conflict.savedBy}` : 'Someone')} onUse={(m) => void applyMerged(m)} />
      )}
    </Drawer>
  );
});

function ChooseAction({
  action, onAction, request, onRequest, isDark, modelName, chosen, planProblem, onChangeModel, onPropose, checks,
}: {
  action: ImprovementAction;
  onAction: (a: ImprovementAction) => void;
  request: string;
  onRequest: (r: string) => void;
  isDark: boolean;
  modelName: string | null;
  chosen: boolean;
  planProblem: string | null;
  onChangeModel: () => void;
  onPropose: () => void;
  checks: number;
}) {
  const s = surface(isDark);
  const asking = action === 'AskForChange';
  return (
    <section style={cardStyle(isDark)} aria-label="Choose an AI action">
      <Text strong style={{ display: 'block' }}>What should change?</Text>
      <Text type="secondary" style={{ display: 'block', fontSize: 12.5, marginBottom: 12 }}>
        {`An AI action never edits the scenario. It proposes lines with their reasons; you keep or drop each line, then save a new version. This version has ${checks} check${checks === 1 ? '' : 's'}.`}
      </Text>
      <div role="radiogroup" aria-label="AI action" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 8 }}>
        {IMPROVEMENT_ACTIONS.map((a) => (
          <button key={a.action} type="button" role="radio" aria-checked={action === a.action} onClick={() => onAction(a.action)}
            style={{ textAlign: 'left', padding: 10, borderRadius: 8, cursor: 'pointer', background: action === a.action ? (isDark ? 'rgba(139, 92, 246, 0.16)' : colors.violet[50]) : s.card,
              border: `1px solid ${action === a.action ? colors.violet[500] : s.line}`, color: s.text }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{a.label}</div>
            <div style={{ fontSize: 12, color: s.muted, marginTop: 2 }}>{a.changes}</div>
          </button>
        ))}
        <button type="button" role="radio" aria-checked={asking} onClick={() => onAction('AskForChange')}
          style={{ textAlign: 'left', padding: 10, borderRadius: 8, cursor: 'pointer', background: asking ? (isDark ? 'rgba(139, 92, 246, 0.16)' : colors.violet[50]) : s.card,
            border: `1px solid ${asking ? colors.violet[500] : s.line}`, color: s.text }}>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>Ask for a change</div>
          <div style={{ fontSize: 12, color: s.muted, marginTop: 2 }}>In your own words. It may change any part, and nothing else.</div>
        </button>
      </div>
      <Input.TextArea
        style={{ marginTop: 12 }}
        autoSize={{ minRows: 2, maxRows: 5 }}
        maxLength={1000}
        value={request}
        onChange={(e) => onRequest(e.target.value)}
        aria-label={asking ? 'What to change' : 'Anything to keep in mind (optional)'}
        placeholder={asking ? 'For example: the consult must name the triage nurse, and the reply must not give advice before it.' : 'Anything to keep in mind (optional)'}
      />
      <div style={{ marginTop: 10, fontSize: 12, display: 'flex', alignItems: 'baseline', gap: 4, flexWrap: 'wrap' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Drafts with</Text>
        <Text strong style={{ fontSize: 12 }}>{modelName ?? 'no Scenario model'}</Text>
        {chosen && <Text type="secondary" style={{ fontSize: 12 }}>(this proposal only)</Text>}
        <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 12 }} onClick={onChangeModel}>Change…</Button>
      </div>
      {planProblem && <Alert type="warning" showIcon title={planProblem} style={{ marginTop: 6 }} />}
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<BulbOutlined />} disabled={(asking && !request.trim()) || !modelName} onClick={onPropose}>Propose a change</Button>
      </div>
    </section>
  );
}

const CONTROL_NAMES: Record<ControlOutcome['name'], string> = {
  Negative: 'Negative control',
  SafeParaphrase: 'Safe-paraphrase control',
};

function ControlLine({ c, reply }: { c: ControlOutcome; reply?: string | null }) {
  const icon = c.state === 'AsIntended' ? <CheckCircleFilled style={{ color: colors.green[600] }} />
    : c.state === 'NotNeeded' ? <MinusCircleOutlined style={{ color: colors.slate[400] }} />
      : <CloseCircleFilled style={{ color: colors.red[600] }} />;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <span style={{ marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: 12.5 }}>
        <Text strong style={{ fontSize: 12.5 }}>{CONTROL_NAMES[c.name]}</Text>
        <Text type="secondary" style={{ fontSize: 12.5 }}>{c.state === 'NotNeeded' ? '' : ', replayed on stored text, no model call'}: </Text>
        {reply && <Text italic style={{ fontSize: 12.5 }}>“{reply}” </Text>}
        <Text style={{ fontSize: 12.5 }}>{c.words}</Text>
      </div>
    </div>
  );
}

function WhyCard({
  view, why, actionLabel, controls, proposal, assessing, isDark,
}: {
  view: ScenarioImprovementView;
  why: string;
  actionLabel: string;
  controls: ControlOutcome[];
  proposal: NonNullable<ReturnType<typeof parseProposal>>;
  assessing: boolean;
  isDark: boolean;
}) {
  const cost = describeCost(view.cost);
  const proven = controls.every((c) => c.state === 'AsIntended' || c.state === 'NotNeeded');
  return (
    <section style={{ ...cardStyle(isDark), borderLeft: `3px solid ${proven ? colors.violet[500] : colors.amber[500]}` }} aria-label="Why">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <Text strong>{proposal.lines.length === 0 ? 'No change proposed' : `Why: ${actionLabel}`}</Text>
        {proposal.lines.length > 0 && <StatusPill tone={proven ? 'green' : 'amber'}>{assessing ? 'Checking…' : proven ? 'Proven on stored text' : 'Unproven'}</StatusPill>}
      </div>
      <div style={{ fontSize: 13.5, marginBottom: 10 }}>{why}</div>
      {proposal.lines.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {controls.map((c) => (
            <ControlLine key={c.name} c={c} reply={c.name === 'Negative' ? proposal.wrongReply?.reply : proposal.safeParaphrase?.reply} />
          ))}
          {!proven && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              An unproven change is never saved in one click: the controls did not show that a wrong reply now fails and a right one still passes.
            </Text>
          )}
        </div>
      )}
      <Text type="secondary" style={{ display: 'block', fontSize: 11.5, marginTop: 10 }}>
        {[
          `Proposed by ${view.answeredBy?.deploymentName ?? view.answeredBy?.modelName ?? 'the Scenario model'}${view.answeredBy?.apiTitle ? ` · ${view.answeredBy.apiTitle}` : ''} (Scenario model)`,
          when(view.proposedAtUtc),
          cost ?? 'Cost not recorded',
          view.attempts > 1 ? `${view.attempts} attempts` : null,
        ].filter(Boolean).join(' · ')}
      </Text>
    </section>
  );
}

function ColumnHeads({ isDark, baseNumber, conflict }: { isDark: boolean; baseNumber?: number | null; conflict: Conflict | null }) {
  const s = surface(isDark);
  const head = (t: string, sub: string) => (
    <div>
      <Text style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: s.muted }}>{t}</Text>
      <div style={{ fontSize: 11.5, color: s.muted }}>{sub}</div>
    </div>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '170px repeat(3, minmax(0, 1fr))', gap: 10, padding: '0 14px' }}>
      <span />
      {head('Original', `version ${baseNumber ?? '?'}, what you opened`)}
      {head('Yours', 'your draft with the AI lines you keep')}
      {head('Current', conflict ? `saved since${conflict.savedAtUtc ? ` · ${when(conflict.savedAtUtc)}` : ''}` : `version ${baseNumber ?? '?'} on the server`)}
    </div>
  );
}

function lineText(l: ImprovementLine): { sign: string; text: string } {
  const after = (l.afterChecks ?? []).map(checkSentence).join('; ');
  switch (l.kind) {
    case 'AddCheck': return { sign: '+', text: after };
    case 'RemoveCheck': return { sign: '−', text: l.beforeCheck ? checkSentence(l.beforeCheck) : '' };
    case 'ReplaceCheck': return { sign: '→', text: after };
    default: return { sign: '', text: l.after ?? '' };
  }
}

function FieldRow({
  field, original, current, kept, onToggle, isDark, narrow, disabled,
}: {
  field: ImprovementField;
  original: EvaluationScenario;
  current: EvaluationScenario | null;
  kept: ReadonlySet<string>;
  onToggle: (id: string, on: boolean) => void;
  isDark: boolean;
  narrow: boolean;
  disabled: boolean;
}) {
  const s = surface(isDark);
  const textOf = (v: EvaluationScenario): string =>
    field.key === 'purpose' ? v.purpose ?? ''
      : field.key.startsWith('turn:') ? v.events.find((e) => e.actor === 'User' && e.turnIndex === Number(field.key.slice(5)))?.text ?? ''
        : '';
  const before = field.key === 'checks' ? null : textOf(original);
  const theyChanged = !!current && (field.key === 'checks'
    ? JSON.stringify(current.checks) !== JSON.stringify(original.checks)
    : textOf(current) !== before);
  const outcome = fieldOutcome(field, kept, theyChanged);
  const removedIds = new Set(field.lines.filter((l) => kept.has(l.id) && l.beforeCheck).map((l) => l.beforeCheck!.id));
  const hasLines = field.lines.length > 0;
  const tag = (t: string) => narrow && <Text style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: s.muted }}>{t}</Text>;

  const originalCell = field.key === 'checks' ? (
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      {original.checks.map((c) => (
        <li key={c.id} style={{ fontSize: 12.5, textDecoration: removedIds.has(c.id) ? 'line-through' : undefined, color: removedIds.has(c.id) ? s.muted : undefined }}>
          {checkSentence(c)}{c.strength === 'Weak' ? <Text type="secondary" style={{ fontSize: 11 }}> · weak</Text> : null}
        </li>
      ))}
    </ul>
  ) : <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{before || '(none)'}</div>;

  const yoursCell = !hasLines ? <Text type="secondary" style={{ fontSize: 12.5 }}>Unchanged</Text> : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {field.lines.map((l) => {
        const t = lineText(l);
        return (
          <label key={l.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, cursor: disabled ? 'default' : 'pointer' }}>
            <Checkbox checked={kept.has(l.id)} disabled={disabled} onChange={(e) => onToggle(l.id, e.target.checked)} aria-label={`Keep ${t.text}`} />
            <span style={{ minWidth: 0, opacity: kept.has(l.id) ? 1 : 0.55 }}>
              {t.sign && <b style={{ marginRight: 4 }}>{t.sign}</b>}
              <span style={{ textDecoration: l.kind === 'RemoveCheck' && kept.has(l.id) ? 'line-through' : undefined }}>{t.text}</span>
              {l.strength === 'Weak' && <Text type="secondary" style={{ fontSize: 11 }}> · weak</Text>}
              <Text type="secondary" style={{ display: 'block', fontSize: 11.5 }}>{l.why}</Text>
            </span>
          </label>
        );
      })}
    </div>
  );

  const currentCell = !current || !theyChanged
    ? <Text type="secondary" style={{ fontSize: 12.5 }}>Same as Original</Text>
    : field.key === 'checks'
      ? <ul style={{ margin: 0, paddingLeft: 16 }}>{current.checks.map((c) => <li key={c.id} style={{ fontSize: 12.5 }}>{checkSentence(c)}</li>)}</ul>
      : <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{textOf(current)}</div>;

  if (!hasLines && !theyChanged) {
    return (
      <section style={{ ...cardStyle(isDark), padding: '8px 14px' }} aria-label={field.label}>
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '170px minmax(0, 1fr) auto', gap: narrow ? 2 : 10, alignItems: 'baseline' }}>
          <Text strong style={{ fontSize: 12.5 }}>{field.label}</Text>
          <div style={{ fontSize: 12.5, color: s.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: narrow ? 'normal' : 'nowrap' }}>
            {field.key === 'checks' ? `${original.checks.length} checks` : before || '(none)'}
          </div>
          <Text style={{ fontSize: 12, color: isDark ? colors.green[500] : colors.green[700] }}>✓ Unchanged: Current is used</Text>
        </div>
      </section>
    );
  }

  return (
    <section style={cardStyle(isDark)} aria-label={field.label}>
      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '170px repeat(3, minmax(0, 1fr))', gap: 10 }}>
        <div>
          <Text strong style={{ fontSize: 13 }}>{field.label}</Text>
          {hasLines && <div><StatusPill tone="violet">{`${field.lines.filter((l) => kept.has(l.id)).length} of ${field.lines.length} kept`}</StatusPill></div>}
        </div>
        <div>{tag('Original')}{originalCell}</div>
        <div style={{ background: hasLines ? (isDark ? 'rgba(139, 92, 246, 0.12)' : colors.violet[50]) : undefined, borderRadius: 6, padding: hasLines ? 6 : 0 }}>{tag('Yours')}{yoursCell}</div>
        <div>{tag('Current')}{currentCell}</div>
      </div>
      <Text style={{ display: 'block', fontSize: 12, marginTop: 8, color: outcome.tone === 'amber' ? (isDark ? colors.amber[500] : colors.amber[700]) : (isDark ? colors.green[500] : colors.green[700]) }}>
        {outcome.tone === 'amber' ? '⚠ ' : '✓ '}{outcome.text}
      </Text>
    </section>
  );
}
